import { CommonModule } from '@angular/common';
import { HttpClientModule } from '@angular/common/http';
import { ModuleWithProviders, NgModule, inject } from '@angular/core';
import { provideTranslocoConfig, provideTranslocoLoader, translocoConfig } from '@ngneat/transloco';
import { provideTranslocoDateTransformer, provideTranslocoLocale, provideTranslocoNumberTransformer } from '@ngneat/transloco-locale';
import { provideTranslocoMessageformat } from '@ngneat/transloco-messageformat';
import { PersistLangConfig, TRANSLOCO_PERSIST_LANG_STORAGE, TranslocoPersistLangModule } from '@ngneat/transloco-persist-lang';
import { CheckoutLocale } from '@platform-frontend/checkout-drop-in';
import { WINDOW } from '@platform-frontend/util';
import {
  TRANSLATION_INTERPOLATION_EXPRESSION_MATCHER,
  defaultTranslocoMarkupTranspilers,
  provideLinkRenderer,
  provideTranslationMarkupTranspiler
} from 'ngx-transloco-markup';
import { DEFAULT_LANGUAGE_CONFIG } from './default-language-config';
import { DateTransformer } from './locale/date-transformer';
import { LANG_TO_LOCALE_MAPPING } from './locale/lang-to-locale-mapping';
import { LocaleInterceptor } from './locale/locale-interceptor';
import { LocaleSetupService, LocaleSetupServiceInitializer } from './locale/locale-setup.service';
import { NumberTransformer } from './locale/number-transformer';
import { BlurCallbackLinkRenderer } from './markup/blur-callback-link-renderer';
import { ClickCallbackLinkRenderer } from './markup/click-callback-link-renderer';
import { DateTranspiler } from './markup/date-transpiler';
import { DefaultLinkRenderer } from './markup/default-link-renderer';
import { EnumerationTranspiler } from './markup/enumeration-transpiler';
import { ExternalLinkRenderer } from './markup/external-link-renderer';
import { LineBreakTranspiler } from './markup/line-break-transpiler';
import { MailtoLinkRenderer } from './markup/mailto-link-renderer';
import { MessageFormatInterpolationExpressionMatcher } from './markup/message-format-interpolation-expression-matcher';
import { PriceTranspiler } from './markup/price-transpiler';
import { SpanTranspiler } from './markup/span-transpiler';
import { CUSTOM_FORMATTERS } from './message-format/custom-formatters';
import { LanguageConfig } from './model/language-config';
import { TranslocoHttpLoader } from './transloco-http-loader';

export const TRANSLOCO_CURRENCY_CONFIG = { minimumFractionDigits: 2, maximumFractionDigits: 2, minimumIntegerDigits: 1 };
const TRANSLOCO_PERSIST_LANG_CONFIG: PersistLangConfig = {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  getLangFn: ({ cachedLang, browserLang, cultureLang, defaultLang }) =>
    (cachedLang ? (inject(LocaleSetupService).isLocaleSupported(cachedLang as CheckoutLocale) ? cachedLang : null) : null) ||
    (browserLang ? (inject(LocaleSetupService).isLocaleSupported(browserLang as CheckoutLocale) ? browserLang : null) : null) ||
    defaultLang,
  storage: {
    provide: TRANSLOCO_PERSIST_LANG_STORAGE,
    useFactory: () => inject(WINDOW).sessionStorage
  }
};

@NgModule({
  imports: [
    CommonModule,
    HttpClientModule,
    // Transloco Persist Lang:
    TranslocoPersistLangModule.forRoot(TRANSLOCO_PERSIST_LANG_CONFIG)
  ]
})
export class TranslocoConfigModule {
  static withConfiguration(
    isPublic: boolean,
    apiUrl: string,
    languageConfig: LanguageConfig = DEFAULT_LANGUAGE_CONFIG,
    enableServerLocalePreference: boolean = false,
    showLanguageNotSupportedNotifications: boolean = false
  ): ModuleWithProviders<TranslocoConfigModule> {
    return {
      ngModule: TranslocoConfigModule,
      providers: [
        // Transloco:
        provideTranslocoConfig(
          translocoConfig({
            availableLangs: languageConfig.availableLangs,
            defaultLang: languageConfig.defaultLang,
            reRenderOnLangChange: true,
            prodMode: isPublic,
            fallbackLang: languageConfig.defaultLang,
            missingHandler: {
              useFallbackTranslation: true,
              logMissingKey: true
            }
          })
        ),
        provideTranslocoLoader(TranslocoHttpLoader),
        // Transloco Messageformat:
        provideTranslocoMessageformat({
          locales: Object.values(LANG_TO_LOCALE_MAPPING),
          customFormatters: CUSTOM_FORMATTERS
        }),
        // Transloco Locale:
        provideTranslocoLocale({
          defaultLocale: LANG_TO_LOCALE_MAPPING[languageConfig.defaultLang],
          langToLocaleMapping: LANG_TO_LOCALE_MAPPING,
          localeConfig: { global: { date: { dateStyle: 'medium' }, currency: TRANSLOCO_CURRENCY_CONFIG } }
        }),
        provideTranslocoDateTransformer(DateTransformer),
        provideTranslocoNumberTransformer(NumberTransformer),
        LocaleSetupServiceInitializer(enableServerLocalePreference, showLanguageNotSupportedNotifications, apiUrl),
        LocaleInterceptor.getProvider(),
        // Transloco Markup (provide the default transloco markup transpilers last to assure correct override behavior):
        {
          provide: TRANSLATION_INTERPOLATION_EXPRESSION_MATCHER,
          useClass: MessageFormatInterpolationExpressionMatcher
        },
        provideLinkRenderer(DefaultLinkRenderer),
        provideLinkRenderer(ExternalLinkRenderer),
        provideLinkRenderer(MailtoLinkRenderer),
        provideLinkRenderer(ClickCallbackLinkRenderer),
        provideLinkRenderer(BlurCallbackLinkRenderer),
        provideTranslationMarkupTranspiler(LineBreakTranspiler),
        provideTranslationMarkupTranspiler(SpanTranspiler),
        provideTranslationMarkupTranspiler(EnumerationTranspiler),
        provideTranslationMarkupTranspiler(DateTranspiler),
        provideTranslationMarkupTranspiler(PriceTranspiler),
        defaultTranslocoMarkupTranspilers() // provide last!
      ]
    };
  }
}
